/**
 * PKCE (Proof Key for Code Exchange) utility.
 *
 * Used by the web and mobile auth flows where the client cannot safely
 * store a client_secret.  Desktop (Tauri) continues to use the password
 * grant with Basic auth because it is a "confidential" (trusted) client.
 *
 * Flow:
 * 1. Generate a random `code_verifier` (high-entropy string).
 * 2. Derive a `code_challenge` = base64url(SHA-256(code_verifier)).
 * 3. Send `code_challenge` + `code_challenge_method=S256` with the auth request.
 * 4. Send `code_verifier` with the token exchange.
 */

/**
 * Generate a cryptographically random code verifier (43-128 chars, URL-safe).
 */
export function generateCodeVerifier(): string {
  const array = new Uint8Array(48);
  crypto.getRandomValues(array);
  return base64UrlEncode(array);
}

/**
 * Derive a SHA-256 code challenge from a code verifier.
 */
export async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder();
  const data = encoder.encode(verifier);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return base64UrlEncode(new Uint8Array(digest));
}

/** Base64-URL encode (no padding). */
function base64UrlEncode(buffer: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < buffer.length; i++) {
    binary += String.fromCharCode(buffer[i]);
  }
  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/** Store / retrieve the verifier for the current auth session.
 *
 * Uses localStorage (not sessionStorage) so the value survives the full-page
 * navigation from the Tauri webview to the OAuth server and back. In Tauri,
 * navigating to a different origin clears sessionStorage for the original
 * origin, which would cause the PKCE exchange to fail on the return trip.
 */
const VERIFIER_KEY = "_pkce_verifier";

export function storeVerifier(verifier: string): void {
  localStorage.setItem(VERIFIER_KEY, verifier);
}

export function retrieveVerifier(): string | null {
  const v = localStorage.getItem(VERIFIER_KEY);
  localStorage.removeItem(VERIFIER_KEY); // one-time use
  return v;
}
